import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "../utils/axios";
import { useBotInfo } from "../contexts/BotContext";
import Navbar from "../components/Navbar";
import LoadingScreen from "../components/LoadingScreen";
import "./AddBot.css";

interface User {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  isAdmin?: boolean;
}

interface AddBotProps {
  user: User;
}

interface Guild {
  id: string;
  name: string;
}

function AddBot({ user }: AddBotProps) {
  const [inviteUrl, setInviteUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [inviteDisabled, setInviteDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const initialGuildIds = useRef<Set<string>>(new Set());
  const navigate = useNavigate();
  const { botInfo } = useBotInfo();

  useEffect(() => {
    fetchInviteUrl();
    fetchInitialGuilds();
  }, []);

  const fetchInitialGuilds = async () => {
    try {
      const response = await axios.get("/api/guilds");
      const guildIds = response.data.guilds.map((g: Guild) => g.id);
      initialGuildIds.current = new Set(guildIds);
    } catch (error) {
      console.error("Error fetching initial guilds:", error);
    }
  };

  const fetchInviteUrl = async () => {
    try {
      const response = await axios.get("/api/bot/invite-url");

      // Check if invites are allowed
      if (response.data.allowed === false) {
        setInviteDisabled(true);
        setErrorMessage(
          response.data.error || "Bot invites are currently disabled",
        );
      } else {
        setInviteUrl(response.data.inviteUrl);
      }
    } catch (error: any) {
      console.error("Error fetching invite URL:", error);

      // Check if it's a 403 (forbidden) response
      if (error.response?.status === 403) {
        setInviteDisabled(true);
        setErrorMessage(
          error.response.data.error || "Bot invites are currently disabled",
        );
      } else {
        setErrorMessage("Failed to load invite URL");
      }
    } finally {
      setLoading(false);
    }
  };

  const checkForNewGuild = async () => {
    try {
      const response = await axios.get("/api/guilds");
      const currentGuilds: Guild[] = response.data.guilds;

      // Check if there's a new guild
      for (const guild of currentGuilds) {
        if (!initialGuildIds.current.has(guild.id)) {
          // New guild found! Navigate to it
          navigate(`/server/${guild.id}`);
          return true;
        }
      }
      return false;
    } catch (error: any) {
      console.error("Error checking for new guild:", error);

      // If we get 401 (unauthorized/session expired), stop checking and redirect to login
      if (error.response?.status === 401) {
        console.error("Session expired, redirecting to login...");
        navigate("/login");
        return true; // Return true to stop the interval
      }

      return false;
    }
  };

  const startChecking = () => {
    setChecking(true);
    const interval = setInterval(async () => {
      const found = await checkForNewGuild();
      if (found) {
        clearInterval(interval);
        setChecking(false);
      }
    }, 2000); // Check every 2 seconds

    // Stop checking after 5 minutes
    setTimeout(() => {
      clearInterval(interval);
      setChecking(false);
    }, 300000);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInviteBot = () => {
    window.open(inviteUrl, "_blank", "width=500,height=800");
    startChecking(); // Start checking for new guild
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="add-bot-page">
      <Navbar user={user} />

      <div className="add-bot-container">
        <Link to="/dashboard" className="back-button">
          ← Back to Dashboard
        </Link>
        <div className="add-bot-card">
          <div className="add-bot-header">
            {botInfo?.avatarURL ? (
              <img
                src={botInfo.avatarURL}
                alt="Bot Avatar"
                className="bot-avatar"
              />
            ) : (
              <div className="bot-icon">🤖</div>
            )}
            <h1>Add Bot to Server</h1>
            <p className="subtitle">
              Invite {botInfo?.username || "Apocalypse Bot"} to your Discord
              server to get started with reaction roles and more!
            </p>
          </div>

          <div className="add-bot-content">
            <div className="feature-list">
              <h3>What you'll get:</h3>
              <ul>
                <li>
                  <span className="feature-icon">🎭</span>
                  <div>
                    <strong>Reaction Roles</strong>
                    <p>Let users self-assign roles by reacting to messages</p>
                  </div>
                </li>
                <li>
                  <span className="feature-icon">⚙️</span>
                  <div>
                    <strong>Web Panel</strong>
                    <p>Easy management through this web interface</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="permissions-info">
              <h3>Required Permissions:</h3>
              <div className="permission-badges">
                <span className="permission-badge">Manage Roles</span>
                <span className="permission-badge">Manage Messages</span>
                <span className="permission-badge">Read Messages</span>
                <span className="permission-badge">Add Reactions</span>
                <span className="permission-badge">View Channels</span>
              </div>
              <p className="permission-note">
                ⚠️ The bot needs these permissions to function properly. You can
                customize them after adding the bot.
              </p>
            </div>

            <div className="invite-actions">
              {inviteDisabled ? (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <p>{errorMessage}</p>
                </div>
              ) : (
                <>
                  <button
                    className="btn btn-primary btn-large"
                    onClick={handleInviteBot}
                  >
                    <span className="btn-icon">➕</span>
                    Invite Bot to Server
                  </button>

                  {checking && (
                    <div className="checking-status">
                      {botInfo?.avatarURL ? (
                        <img
                          src={botInfo.avatarURL}
                          alt="Bot Avatar"
                          className="spinner-avatar"
                        />
                      ) : (
                        <div className="spinner-small"></div>
                      )}
                      <p>
                        Waiting for bot to join server... You'll be
                        automatically redirected.
                      </p>
                    </div>
                  )}

                  <div className="invite-link-section">
                    <p className="or-text">Or copy the invite link:</p>
                    <div className="invite-link-container">
                      <input
                        type="text"
                        value={inviteUrl}
                        readOnly
                        className="invite-link-input"
                      />
                      <button
                        className={`btn btn-secondary ${copied ? "copied" : ""}`}
                        onClick={handleCopyLink}
                      >
                        {copied ? "✓ Copied!" : "📋 Copy"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="help-section">
              <h4>Need help?</h4>
              <p>
                Make sure you have <strong>Manage Server</strong> permission on
                the server you want to add the bot to. After inviting the bot,
                return to the <a href="/dashboard">Dashboard</a> to start
                configuring reaction roles!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddBot;
